import type { RechargeSettingsSummary } from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent, type JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { SaveButton } from '@/components/ui/save-button.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import {
  fetchRechargeSettings,
  testRechargeConnection,
  testRechargeSlack,
} from './api-recharge.js';
import { RechargeFeaturesSection } from './recharge-features-section.js';
import { RechargeKlaviyoSection } from './recharge-klaviyo-section.js';
import { ConnectionTestButton, SecretField } from './recharge-parts.js';
import { RechargeWebhooksSection } from './recharge-webhooks-section.js';
import { SettingsSectionSkeleton } from './settings-parts.js';
import { useSaveRechargeSettings } from './use-save-recharge-settings.js';

function ConnectionSection({ settings }: { settings: RechargeSettingsSummary }): JSX.Element {
  const [token, setToken] = useState('');
  const save = useSaveRechargeSettings(() => {
    setToken('');
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const apiToken = token.trim();
    if (apiToken) {
      save.mutate({ apiToken });
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <PageSection
        title="Connexion"
        description="Token de l’API Recharge, utilisé pour lire et corriger les abonnements."
        footer={<SaveButton isDirty={token.trim() !== ''} isLoading={save.isPending} />}
      >
        <div className="flex flex-col gap-4">
          <SecretField
            id="recharge-api-token"
            label="Token API"
            configured={settings.apiTokenConfigured}
            value={token}
            onChange={setToken}
            hint={
              settings.apiTokenConfigured
                ? 'Jamais réaffiché : laissez le champ vide pour conserver le token enregistré.'
                : undefined
            }
          />
          <ConnectionTestButton label="Tester la connexion" run={testRechargeConnection} />
        </div>
      </PageSection>
    </form>
  );
}

const SLACK_URL_ERROR = 'Saisissez l’URL complète du webhook, qui commence par https://';

function SlackSection({ settings }: { settings: RechargeSettingsSummary }): JSX.Element {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const save = useSaveRechargeSettings(() => {
    setUrl('');
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const slackWebhookUrl = url.trim();
    if (!slackWebhookUrl) {
      return;
    }
    if (!/^https:\/\/\S+$/.test(slackWebhookUrl)) {
      setError(SLACK_URL_ERROR);
      document.getElementById('recharge-slack-url')?.focus();
      return;
    }
    save.mutate({ slackWebhookUrl });
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <PageSection
        title="Slack abonnements"
        description="Messages destinés à l’équipe Mium : paiements échoués, cantons et migrations."
        footer={<SaveButton isDirty={url.trim() !== ''} isLoading={save.isPending} />}
      >
        <div className="flex flex-col gap-4">
          <SecretField
            id="recharge-slack-url"
            label="URL du webhook Slack"
            configured={settings.slackWebhookConfigured}
            value={url}
            inputMode="url"
            onChange={(next) => {
              setUrl(next);
              setError(undefined);
            }}
            hint="Canal #mium-alertes-abos, distinct des alertes Flux"
            error={error}
          />
          <ConnectionTestButton label="Envoyer un message de test" run={testRechargeSlack} />
        </div>
      </PageSection>
    </form>
  );
}

/**
 * Onglet **Recharge** (`06` §8.6) : une carte par section, chacune avec son
 * enregistrement, qui n'envoie que sa partie de `PUT /api/recharge/settings`.
 */
export function RechargeTab(): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.rechargeSettings.summary(),
    queryFn: fetchRechargeSettings,
  });

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-6">
        <SettingsSectionSkeleton fields={1} />
        <SettingsSectionSkeleton fields={2} />
        <SettingsSectionSkeleton fields={1} />
        <SettingsSectionSkeleton fields={3} />
        <SettingsSectionSkeleton fields={2} />
      </div>
    );
  }

  if (query.isError) {
    return (
      <PageSection title="Recharge">
        <ErrorState
          message={
            query.error instanceof ApiError ? query.error.message : 'Impossible de charger les réglages Recharge.'
          }
          detail={query.error instanceof ApiError ? query.error.detail : undefined}
          onRetry={() => {
            void query.refetch();
          }}
          className="border-0 bg-transparent py-6"
        />
      </PageSection>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ConnectionSection settings={query.data} />
      <RechargeWebhooksSection settings={query.data} />
      <SlackSection settings={query.data} />
      <RechargeFeaturesSection settings={query.data} />
      <RechargeKlaviyoSection settings={query.data} />
    </div>
  );
}
